import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '../components/layout';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { api } from '../lib/api';
import { formatMoney } from '../lib/utils';

interface Overview {
  traffic: { sessions: number; users: number; productViews: number; searches: number };
  funnel: { step: string; sessions: number }[];
  sales: {
    orders: number;
    revenueMinor: number;
    netRevenueMinor: number;
    marginMinor: number;
    marginPct: number | null;
    costCoveragePct: number | null;
    refundsMinor: number;
    aovMinor: number | null;
    customers: number;
    newCustomers: number;
  };
  conversionPct: number | null;
}

interface Cohort {
  cohort: string;
  customers: number;
  ltvMinor: (number | null)[];
}

interface ProductRow {
  variantId: string;
  sku: string;
  name: string;
  views: number;
  addToCarts: number;
  purchases: number;
  unitsSold: number;
  revenueMinor: number;
  marginMinor: number;
  viewToPurchasePct: number | null;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

function Kpi({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-lg border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-xl font-semibold">{value}</p>
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

/** Аналитика (§18): посещения и воронка (с согласием), продажи и маржа, когорты, товары. */
export function AnalyticsPage() {
  const { t, i18n } = useTranslation();
  const [from, setFrom] = useState(iso(new Date(Date.now() - 29 * 86_400_000)));
  const [to, setTo] = useState(iso(new Date()));
  const [market, setMarket] = useState('');
  const query = `from=${from}&to=${to}${market ? `&market=${market}` : ''}&locale=${i18n.language}`;
  const overview = useQuery({
    queryKey: ['analytics', 'overview', query],
    queryFn: () => api<Overview>(`/analytics/overview?${query}`),
  });
  const cohorts = useQuery({
    queryKey: ['analytics', 'cohorts'],
    queryFn: () => api<{ items: Cohort[] }>('/analytics/cohorts?months=6'),
  });
  const products = useQuery({
    queryKey: ['analytics', 'products', query],
    queryFn: () => api<{ items: ProductRow[] }>(`/analytics/products?${query}`),
  });
  const money = (minor: number | null | undefined) =>
    minor === null || minor === undefined ? '—' : formatMoney(minor, 'EUR', i18n.language);
  const pct = (value: number | null | undefined) =>
    value === null || value === undefined ? '—' : `${String(value)}%`;
  const o = overview.data;
  const top = o?.funnel[0]?.sessions ?? 0;

  return (
    <>
      <PageHeader title={t('an.title')} description={t('an.hint')} />
      <div className="mb-4 flex flex-wrap items-end gap-3 text-sm">
        <Field label={t('ord.from')}>
          {(id) => (
            <Input
              id={id}
              type="date"
              value={from}
              onChange={(e) => {
                setFrom(e.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('ord.to')}>
          {(id) => (
            <Input
              id={id}
              type="date"
              value={to}
              onChange={(e) => {
                setTo(e.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('an.market')}>
          {(id) => (
            <Select
              id={id}
              value={market}
              onChange={(e) => {
                setMarket(e.target.value);
              }}
            >
              <option value="">{t('an.allMarkets')}</option>
              {['LV', 'LT', 'EE'].map((m) => (
                <option key={m}>{m}</option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      <div className="grid gap-6">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-5">
          <Kpi
            label={t('an.revenue')}
            value={money(o?.sales.revenueMinor)}
            hint={t('an.withVat')}
          />
          <Kpi label={t('an.orders')} value={String(o?.sales.orders ?? '—')} />
          <Kpi label={t('an.aov')} value={money(o?.sales.aovMinor)} />
          <Kpi
            label={t('an.margin')}
            value={money(o?.sales.marginMinor)}
            hint={`${pct(o?.sales.marginPct)} · ${t('an.costCoverage', { pct: pct(o?.sales.costCoveragePct) })}`}
          />
          <Kpi label={t('an.refunds')} value={money(o?.sales.refundsMinor)} />
          <Kpi
            label={t('an.sessions')}
            value={String(o?.traffic.sessions ?? '—')}
            hint={t('an.consentOnly')}
          />
          <Kpi label={t('an.conversion')} value={pct(o?.conversionPct)} />
          <Kpi
            label={t('an.customers')}
            value={String(o?.sales.customers ?? '—')}
            hint={t('an.newCustomers', { count: o?.sales.newCustomers ?? 0 })}
          />
          <Kpi label={t('an.productViews')} value={String(o?.traffic.productViews ?? '—')} />
          <Kpi label={t('an.searches')} value={String(o?.traffic.searches ?? '—')} />
        </div>

        <Card>
          <CardHeader title={t('an.funnel')} description={t('an.funnelHint')} />
          <CardContent className="grid gap-2 text-sm">
            {(o?.funnel ?? []).map((f) => {
              const share = top ? f.sessions / top : 0;
              return (
                <div key={f.step} className="grid grid-cols-[10rem_1fr_6rem] items-center gap-3">
                  <span>{t(`an.steps.${f.step as 'session'}`)}</span>
                  <div className="h-5 rounded bg-muted" aria-hidden>
                    <div
                      className="h-5 rounded bg-primary"
                      style={{ width: `${String(share * 100)}%` }}
                    />
                  </div>
                  <span className="text-right">
                    {f.sessions} · {top ? `${String(Math.round(share * 1000) / 10)}%` : '—'}
                  </span>
                </div>
              );
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader title={t('an.cohorts')} description={t('an.cohortsHint')} />
          <CardContent className="overflow-x-auto text-sm">
            <table className="w-full">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="text-left font-normal">{t('an.cohort')}</th>
                  <th className="text-right font-normal">{t('an.customers')}</th>
                  {Array.from({ length: 6 }, (_, i) => (
                    <th key={i} className="text-right font-normal">
                      {t('an.month', { n: i })}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(cohorts.data?.items ?? []).map((c) => (
                  <tr key={c.cohort} className="border-t">
                    <td className="py-1.5">{c.cohort}</td>
                    <td className="text-right">{c.customers}</td>
                    {c.ltvMinor.map((v, i) => (
                      <td key={i} className="text-right">
                        {money(v)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title={t('an.products')} description={t('an.productsHint')} />
          <CardContent className="overflow-x-auto text-sm">
            <table className="w-full">
              <thead className="text-xs text-muted-foreground">
                <tr>
                  <th className="text-left font-normal">{t('an.product')}</th>
                  <th className="text-right font-normal">{t('an.views')}</th>
                  <th className="text-right font-normal">{t('an.carts')}</th>
                  <th className="text-right font-normal">{t('an.purchases')}</th>
                  <th className="text-right font-normal">{t('an.viewToPurchase')}</th>
                  <th className="text-right font-normal">{t('an.netRevenue')}</th>
                  <th className="text-right font-normal">{t('an.margin')}</th>
                </tr>
              </thead>
              <tbody>
                {(products.data?.items ?? []).map((p) => (
                  <tr key={p.variantId} className="border-t">
                    <td className="py-1.5">
                      {p.name}{' '}
                      <span className="font-mono text-xs text-muted-foreground">{p.sku}</span>
                    </td>
                    <td className="text-right">{p.views}</td>
                    <td className="text-right">{p.addToCarts}</td>
                    <td className="text-right">{p.purchases}</td>
                    <td className="text-right">{pct(p.viewToPurchasePct)}</td>
                    <td className="text-right">{money(p.revenueMinor)}</td>
                    <td className="text-right">{money(p.marginMinor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {products.data?.items.length === 0 ? (
              <p className="text-muted-foreground">{t('an.noProducts')}</p>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
